import Css from "./style.module.scss";

import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData,
  getSelectedBusinessIntegrationServiceData
} from "selectors/businesses";
import { getAccountsData } from "selectors/accounts";
import { getActiveOrganization, getActiveUsersData } from "selectors/organizations";
import { getContactsData } from "selectors/contacts";
import { getCurrenciesData } from "selectors/metaData";
import { getInitialState, updateState } from "./utils";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AccountUtils from "utils/Accounts";
import Async from "utils/Async";
import AuBusinessNumbers from "utils/AuBusinessNumbers";
import BusinessesActions from "actions/BusinessesActions";
import ContactsActions from "actions/ContactsActions";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentContext from "contexts/DocumentContext";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import DocumentsActions from "actions/DocumentsActions";
import DuplicatedDocumentsWindow from "./lib/DuplicatedDocumentsWindow";
import EditForm from "./lib/EditForm";
import Form from "nlib/common/Form";
import Header from "./lib/Header";
import Money from "utils/Money";
import Objects from "utils/Objects";
import OldEditForm from "./lib/OldEditForm";
import PdfViewer from "nlib/common/PdfViewer";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Texts from "utils/Texts";
import TransactionTypes from "const/TransactionTypes";
import UiActions from "actions/UiActions";
import moment from "moment";
import useShowCommonModal from "hooks/useShowCommonModal";
import useShowModal from "hooks/useShowModal";

const DOCUMENT_FROZEN_STATUSES = [
  DataStatuses.TO_REPORT,
  DataStatuses.EXCLUDED,
  DataStatuses.EXPORTED
];

const EDITABLE_DOCUMENT_PROPS = [
  "amountBase",
  "amountVat",
  "amount",
  "exportAs",
  "detailedTaxRates",
  "address",
  "currency",
  "dueDate",
  "issueDate",
  "lineItems",
  "linkedDocumentsIds",
  "location",
  "originalDocumentId",
  "paymentType",
  "paymentDate",
  "payTo",
  "paid",
  "status",
  "type",
  "vendorId",
  "privateNote",
  "vatId"
];

const DOCUMENT_TYPE_VARIANTS = {
  [DocumentTypes.INVOICE]: { type: DocumentTypes.INVOICE, paymentType: DocumentPaymentTypes.SELL },
  [DocumentTypes.BILL]: { type: DocumentTypes.INVOICE, paymentType: DocumentPaymentTypes.BUY },
  [DocumentTypes.RECEIPT]: { type: DocumentTypes.RECEIPT, paymentType: DocumentPaymentTypes.BUY },
  [DocumentTypes.SALES_RECEIPT]: { type: DocumentTypes.SALES_RECEIPT, paymentType: DocumentPaymentTypes.SELL }
};

const DEFAULTS = {
  dueDate: "",
  issueDate: "",
  originalDocumentId: ""
};

const normalizeVatId = (vatId) => {
  return vatId && `${vatId}`.replace(/\D/g, "");
};

const EditDocument = ({ openedInWindow, documentId, extractedDocumentsData, refetchDocuments, openDocument, onClose }) => {
  const componentMounted = useRef(false);

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const contactsData = useSelector(getContactsData);

  const currenciesData = useSelector(getCurrenciesData);

  const accountsData = useSelector(getAccountsData);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const {
    extraData: { integrationService } = {}
  } = useSelector(getSelectedBusinessData);

  const [
    duplicatedDocumentsModalData,
    showDuplicatedDocumentModal,
    onCloseManageDuplicationsModal
  ] = useShowModal();

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const selectedBusinessIntegrationServiceData = useSelector(getSelectedBusinessIntegrationServiceData) || {};

  const usersData = useSelector(getActiveUsersData);

  const showCommonModal = useShowCommonModal();

  const [{ loading, documentData }, setState] = useState({});

  const resetDuplicatedDocumentsIds = !!(documentData && (documentData.duplicatedDocumentsIds || []).length);

  const documentFrozen = DOCUMENT_FROZEN_STATUSES.includes(documentData?.status);

  const initialValues = useMemo(() => {
    return Objects.getProps(documentData, EDITABLE_DOCUMENT_PROPS, DEFAULTS);
  }, [documentData]);

  const onDuplicatedDocuments = useCallback(async(response) => {
    const modalResult = await showDuplicatedDocumentModal(response);

    if (modalResult) {
      const { deleteDocument } = modalResult;

      if (deleteDocument) {
        if (!documentData?.attachment?.key) {
          onClose(true);

          return;
        }

        const deleteModalResult = await showCommonModal({ text: messages.documentDeleteConfirm, confirm: true });

        if (deleteModalResult) {
          await dispatch(DocumentsActions.deleteDocument(documentId));
          onClose(true);
        }
      } else {
        await dispatch(DocumentsActions.editDocument(documentId, { duplicatedDocumentsIds: [] }, false, false, false, true));
        if (refetchDocuments) await refetchDocuments();

        const updatedResponse = await dispatch(DocumentsActions.fetchDocument(documentId));

        setState((prev) => ({
          ...prev,
          documentData: getInitialState(
            updatedResponse,
            { xeroBusiness, quickBooksBusiness, countryCode, currenciesData, rpaMode, usersData }
          ),
          loading: false
        }));
      }
    } else {
      onClose();
    }
  }, [
    dispatch,
    documentId,
    messages.documentDeleteConfirm,
    onClose,
    refetchDocuments,
    showDuplicatedDocumentModal,
    showCommonModal,
    usersData,
    xeroBusiness,
    quickBooksBusiness,
    countryCode,
    currenciesData,
    documentData?.attachment?.key,
    rpaMode
  ]);

  const fetchData = useCallback(async() => {
    setState((prev) => ({ ...prev, loading: true }));

    const response = await dispatch(DocumentsActions.fetchDocument(documentId));

    if (!componentMounted.current) return;
    if (!response) {
      onClose();

      return;
    }
    if (response.duplicatedDocumentsIds?.length) {
      onDuplicatedDocuments(response);
    }
    setState((prev) => ({
      ...prev,
      documentData: getInitialState(response, {
        xeroBusiness,
        quickBooksBusiness,
        countryCode,
        currenciesData,
        rpaMode,
        usersData
      }),
      loading: false
    }));
  }, [
    dispatch,
    documentId,
    onClose,
    xeroBusiness,
    quickBooksBusiness,
    countryCode,
    currenciesData,
    rpaMode,
    usersData,
    onDuplicatedDocuments
  ]);

  const handleFormChange = useCallback((name, value, values) => {
    const { status, recogniseData } = documentData;

    let data = { [name]: value };

    if (name === "documentType") {
      const { type, paymentType } = DOCUMENT_TYPE_VARIANTS[value];

      data = { type, paymentType };
    }

    const updatedData = updateState(data, values, {
      recogniseData,
      countryCode,
      quickBooksBusiness,
      xeroBusiness,
      integrationService,
      contactsData,
      status
    });

    return { ...values, ...updatedData };
  }, [contactsData, countryCode, documentData, integrationService, quickBooksBusiness, xeroBusiness]);

  const handleFormSubmit = useCallback(async(props) => {
    let {
      values,
      extraData: { toReport, toExclude } = {}
    } = props;

    if (documentData.paidTransactions.length) {
      const transactionShouldBeType = values.paymentType === DocumentPaymentTypes.BUY
        ? TransactionTypes.WITHDRAW
        : TransactionTypes.DEPOSIT;

      const mismatchedTransaction = documentData.paidTransactions.find(({ type }) => {
        return type !== transactionShouldBeType;
      });

      if (mismatchedTransaction) {
        const modalResult = await showCommonModal({
          text: Texts.replaceVars(messages.documentPairedTransactionTypeMismatch, {
            transactionType: (
              (mismatchedTransaction.type === TransactionTypes.DEPOSIT ? uiTexts.deposit : uiTexts.withdraw) || ""
            ).toLowerCase(),
            documentType: (
              (values.paymentType === DocumentPaymentTypes.BUY ? uiTexts.buyOperation : uiTexts.sellOperation) || ""
            ).toLowerCase()
          }),
          headerText: uiTexts.transactionsTypeMismatch,
          confirm: true,
          okButtonText: uiTexts.unpairTransactionAndApprove
        });

        if (modalResult) {
          setState((prev) => ({ ...prev, loading: true }));
          await Async.runInSequence(documentData.paidTransactions.map(({ id }) => {
            return () => dispatch(BusinessesActions.unpairMatches(id, documentId));
          }));
          setState((prev) => ({ ...prev, loading: false }));
        } else {
          return null;
        }
      }

      const paidTransactionsAmount = documentData.paidTransactions.reduce((acc, transaction) => {
        return acc + transaction.amount;
      }, 0);

      if (Money.toMoneyNumber(values.amount, true, Money.getCurrencyPrecision(values.currency))
        !== Money.toMoneyNumber(paidTransactionsAmount, true, Money.getCurrencyPrecision(values.currency))) {
        const modalResult = await showCommonModal({
          text: messages.documentAmountMismatch,
          headerText: uiTexts.warning,
          confirm: true
        });

        if (!modalResult) return null;
      }
    }
    if (toReport) {
      if (selectedBusinessBookCloseDate && values.issueDate
          && moment.utc(values.issueDate).isSameOrBefore(moment.utc(selectedBusinessBookCloseDate))) {
        const closedBookTimeText = moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT);

        dispatch(
          UiActions.showModal(
            Texts.replaceVars(messages.closeBookWarningForDocs, {
              date: closedBookTimeText,
              service: selectedBusinessIntegrationServiceData.label
            }),
            uiTexts.warning
          )
        );

        return null;
      }
    }
    if (toExclude && values.status === DataStatuses.EXPORTED) {
      const modalResult = await dispatch(
        UiActions.showModal(messages.changeExportedStatusConfirm, uiTexts.confirm, true, "sm")
      );

      if (!modalResult) return null;
    }
    if (values.payTo?.serviceAccountId) {
      const accountData = AccountUtils.findByServiceAccountId(accountsData, values.payTo.serviceAccountId);

      const paymentAccountCurrency = accountData?.currency;

      if (paymentAccountCurrency && paymentAccountCurrency !== values.currency) {
        const modalResult = await dispatch(UiActions.showPaymentAccountCurrencyMismatchWindow({
          docAmount: Money.toMoneyNumber(values.amount || 0, true, Money.getCurrencyPrecision(values.currency)).toLocaleString(),
          docCurrency: values.currency,
          paymentAccountName: accountData?.displayName || accountData?.name || uiTexts.unknown,
          paymentAccountCurrency
        }));

        if (!modalResult) return null;
        values = { ...values, currency: paymentAccountCurrency };
      }
    }

    const documentVatId = ((countryCode === Countries.AU || countryCode === Countries.NZ)
      && values.paymentType === DocumentPaymentTypes.BUY && values.vatId?.trim()) || "";

    if (documentVatId) {
      if (values.abnStatusFetching) return null;

      const { id: addressId, type, name, vatId: vendorVatId } = values.address || {};

      const vendorVatIdNormalized = normalizeVatId(vendorVatId);

      const normalizedVatId = normalizeVatId(documentVatId);

      if (!!addressId && !!normalizedVatId && values.abnActive && AuBusinessNumbers.validate(normalizedVatId)
        && normalizedVatId !== vendorVatIdNormalized
      ) {
        const modalResult = await showCommonModal({
          text: Texts.replaceVars(messages.abnMismatch, { name: values.address?.name }),
          headerText: uiTexts.warning,
          confirm: true,
          okButtonText: uiTexts.updateVendorAbn,
          cancelButtonText: uiTexts.keepVendorAbn
        });

        if (modalResult) {
          setState((prev) => ({ ...prev, loading: true }));
          await dispatch(ContactsActions.editContact(
            addressId,
            { name, type, vatId: AuBusinessNumbers.format(normalizedVatId) }
          ));
        }
      }
    }
    setState((prev) => ({ ...prev, loading: true }));

    const result = await dispatch(
      DocumentsActions.editDocument(
        documentId,
        {
          ...values,
          ...(resetDuplicatedDocumentsIds ? { duplicatedDocumentsIds: [] } : null),
          vatId: documentVatId || null,
          amount: values.amount || 0,
          amountBase: values.amountBase || 0,
          amountVat: values.amountVat || 0,
          paid: values.paid,
          lineItems: values.lineItems && values.lineItems
            .filter((lineItem) => lineItem.description.trim()).map((lineItem) => ({
              ...lineItem,
              category: values.type === DocumentTypes.SALES_RECEIPT ? undefined : lineItem.category,
              item: lineItem.item?.id ? lineItem.item : undefined
            }))
        },
        toReport,
        toExclude
      )
    );

    if (result) onClose(true);
    else setState((prev) => ({ ...prev, loading: false }));

    return result;
  }, [
    documentData?.paidTransactions,
    documentId,
    resetDuplicatedDocumentsIds,
    selectedBusinessBookCloseDate,
    selectedBusinessIntegrationServiceData.label,
    messages,
    accountsData,
    uiTexts,
    dispatch,
    onClose,
    showCommonModal,
    countryCode
  ]);

  const handleSetLoading = useCallback((loadingState) => {
    setState((prev) => ({ ...prev, loading: loadingState }));
  }, [setState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    componentMounted.current = true;
    document.body.setAttribute("data-edit-document", "");

    return () => {
      componentMounted.current = false;
      document.body.removeAttribute("data-edit-document");
    };
  }, []);
  if (!documentData) {
    return (
      <div className={Css.editDocument}>
        <Preloader />
      </div>
    );
  }

  return (
    <>
      <div className={Css.editDocument}>
        <Form
          validateOnChange
          initialValidation
          disabled={loading}
          className={Css.form}
          initialValues={initialValues}
          onChange={countryCode === Countries.CZ ? handleFormChange : undefined}
          onSubmit={handleFormSubmit}>
          {() => {
            return (
              <DocumentContext.Provider value={documentData}>
                <Header
                  openedInWindow={openedInWindow}
                  disabled={loading}
                  documentData={documentData}
                  extractedDocumentsData={extractedDocumentsData}
                  resetDuplicatedDocumentsIds={resetDuplicatedDocumentsIds}
                  openDocument={openDocument}
                  onClose={onClose} />
                <div className={Css.content}>
                  {countryCode === Countries.CZ
                    ? (
                      <>
                        <div className={Css.documentCol}>
                          {documentData?.attachment && (
                            <PdfViewer
                              className={Css.pdfViewer}
                              attachment={documentData.attachment} />
                          )}
                        </div>
                        <div className={Css.formCol}>
                          <OldEditForm
                            disabled={loading}
                            documentData={documentData} />
                        </div>
                      </>
                    )
                    : (
                      <EditForm
                        disabled={loading}
                        documentFrozen={documentFrozen}
                        documentData={documentData}
                        setLoading={handleSetLoading}
                        fetchData={fetchData}
                        refetchDocuments={refetchDocuments} />
                    )}
                </div>
              </DocumentContext.Provider>
            );
          }}
        </Form>
      </div>
      {duplicatedDocumentsModalData && (
        <DuplicatedDocumentsWindow
          documentData={duplicatedDocumentsModalData}
          onClose={onCloseManageDuplicationsModal} />
      )}
    </>
  );
};

export default React.memo(EditDocument);

export { EDITABLE_DOCUMENT_PROPS };
